import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";

export default function AboutPage() {
  useDocumentTitle("À propos", "ShinRai Consulting : cabinet de conseil indépendant. 15 ans d'expertise tri-disciplinaire Lean / Neurosciences / IA. Notre engagement : rendre les organisations autonomes en 6 mois.");
  return (
    <main className="pt-32 pb-24 bg-pearl min-h-screen" data-testid="about-page">
      <div className="max-w-6xl mx-auto px-6 sm:px-10">
        <div className="font-mono-overline text-mute mb-4">À propos</div>
        <h1 className="font-serif text-5xl sm:text-7xl leading-[0.98] tracking-tight max-w-4xl" data-testid="about-title">
          Pas un cabinet de conseil <em className="text-mute not-italic font-light">de plus.</em>
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-20">
          <div className="lg:col-span-7">
            <div className="prose-content text-lg leading-relaxed text-ink space-y-6">
              <p>
                ShinRai (信頼 — « confiance ») est né d'un constat simple : la
                plupart des transformations échouent non pas parce que les
                méthodes sont mauvaises, mais parce qu'elles ignorent un côté
                de l'équation.
              </p>
              <p>
                <strong>Le Lean</strong> sans neurosciences se heurte aux
                résistances humaines. <strong>Les neurosciences</strong> sans
                Lean restent théoriques. <strong>L'IA</strong> sans les deux
                premiers automatise du chaos.
              </p>
              <p>
                Nous avons construit notre méthode après 15 ans passés à voir
                ces approches en silos — chez les Big 4, dans les ETI
                industrielles, dans les startups. Aujourd'hui, ShinRai aide
                les dirigeants à reprendre le pilotage
                de leur organisation. Sans esbroufe. Sans dépendance.
              </p>
              <p>
                Notre engagement : à la fin des 6 mois, tes équipes pilotent
                seules. Nous sortons. Si ce n'est pas le cas, nous restons
                gratuitement jusqu'à ce que ce le soit.
              </p>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden bg-pearl-dark sticky top-32">
              <img
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=900&q=80"
                alt="Approche ShinRai"
                className="w-full h-full object-cover"
              />
            </div>
          </aside>
        </div>

        {/* Values */}
        <section className="mt-32" data-testid="about-values">
          <div className="font-mono-overline text-mute mb-3">Nos valeurs</div>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight mb-16 max-w-3xl">
            Quatre principes <em className="text-mute not-italic font-light">non négociables.</em>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10">
            {[
              { n: "01", t: "Honnêteté technique", d: "Si une mission n'est pas pour nous, nous le disons. Si une voie est plus rapide que la nôtre, nous l'indiquons." },
              { n: "02", t: "Autonomie comme but", d: "Notre métier est de te rendre indépendant — pas de te retenir. Chaque mission a une date de sortie." },
              { n: "03", t: "Mesurer, toujours", d: "Pas d'effet d'annonce. Indicateurs avant, indicateurs après. Le ROI est calculé, pas estimé." },
              { n: "04", t: "Le cerveau d'abord", d: "Toute transformation passe par le neurosystème humain. Nous l'éclairons et le respectons." },
            ].map((v) => (
              <div key={v.n} className="bg-pearl p-10" data-testid={`value-${v.n}`}>
                <div className="font-serif text-5xl text-ink/15 mb-6">{v.n}</div>
                <h3 className="font-serif text-2xl mb-4">{v.t}</h3>
                <p className="text-mute leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-32 bg-ink text-pearl p-12 sm:p-20 grain relative overflow-hidden">
          <div className="max-w-3xl">
            <div className="font-mono-overline text-pearl/40 mb-4">Discutons</div>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight mb-6">
              Tu veux savoir si ShinRai <em className="text-pearl/55 not-italic font-light">est fait pour toi ?</em>
            </h2>
            <p className="text-pearl/65 mb-8">
              Un diagnostic de 45 minutes. Aucune obligation. Aucune CB. Juste un échange technique honnête.
            </p>
            <Link to="/#diagnostic" className="btn-white" data-testid="about-cta">
              Réserver un diagnostic <ArrowUpRight size={16} />
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}
